import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { ApplicationService } from '../../service/ApplicationService';
import LanguageService, { translatedMessage } from '../../service/LanguageService';

import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import GeneralUtils from '../../utilities/GeneralUtils';
import ApiService from '../../service/ApiService';

const emptyClarification = {
    id: null,
    application: {
        id: null
    },
    detail: '',
    type: '',
    folder: {
        id: null
    }
};

const ClarificationDialog = (props) => {
    const [clarification, setClarification] = useState({});

    const toast = useRef(null);

    const applicationService = useMemo(() => new ApplicationService(), []);

    useEffect(() => {
        if (props.clarification) {
            setClarification(props.clarification);
        } else {
            emptyClarification.application.id = props.applicationId;
            emptyClarification.type = props.type;
            setClarification(emptyClarification);
        }
    }, [props]);

    const hideDialog = () => {
        if (typeof props.onHide === 'function') {
            props.onHide();
        }
        formik.resetForm();
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: clarification,
        validate: (data) => {
            let errors = {};

            if (!data.detail) {
                errors.detail = translatedMessage('form.error.detail.required');
            }
            return errors;
        },
        onSubmit: (data) => {
            saveClarification(data);
            hideDialog();
            setClarification({});
            formik.resetForm();
        }
    });

    const saveClarification = (data) => {
        applicationService.saveClarification(data)
            .then((savedClarification) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(savedClarification);
                }
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            });
    };

    const setDescriptionValue = (detail) => {
        formik.setFieldValue('detail', detail);
    }

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const formDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="field-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={props.visible}
                header={'REQUEST' === props.type ? translatedMessage('application.clarification.request') : translatedMessage('application.clarification.response')}
                modal
                className="pcn-dialog p-fluid"
                footer={formDialogFooter}
                onHide={hideDialog}
            >
                <form id="field-form" onSubmit={formik.handleSubmit}>
                    <div className="field pcn-sun-editor w-100 mb-0">
                        <label htmlFor="description" className='font-bold'>{translatedMessage('application.clarification.detail')} *</label>
                        <SunEditor
                            id="description" name="description"
                            lang={LanguageService.getCurrentLanguage()}
                            height="300px"
                            width='auto'
                            defaultValue={formik.values.detail}
                            placeholder={""}
                            setOptions={{
                                buttonList: GeneralUtils.getSunEditorButtons(),
                                font: GeneralUtils.getSunEditorFonts(),
                                defaultStyle: GeneralUtils.getSunEditorDefaultStyle(),
                                charCounter: ApiService.getIsProduction() ? true : false,
                                maxCharCount: ApiService.getIsProduction() ? 10000 : null
                            }}
                            onChange={setDescriptionValue}
                        />
                    </div>
                    {getFormErrorMessage('detail')}
                </form>
            </Dialog>
        </>
    );
};

export default ClarificationDialog;
